import React, { useState, useEffect, useRef } from 'react';
import { UIResult } from '../../types/index';

interface UrinaryIncontinenceAssessmentProps {
  onComplete: (result: UIResult) => void;
  onNext: () => void;
}

interface UIResponses {
  hasProblem?: boolean;
}

type UIStep = 
  | 'intro' 
  | 'primary_question'
  | 'results'
  | 'carer_correction'
  | 'completed';

const UrinaryIncontinenceAssessment: React.FC<UrinaryIncontinenceAssessmentProps> = ({ onComplete, onNext }) => {
  const [currentStep, setCurrentStep] = useState<UIStep>('intro');
  const [responses, setResponses] = useState<Partial<UIResponses>>({});
  const hasCompletedRef = useRef<boolean>(false);
  const [carerCorrection, setCarerCorrection] = useState({
    problemCorrect: null as boolean | null
  });

  // 评估逻辑实现 - 按WHO ICOPE标准简化
  const evaluateUrinaryIncontinence = (): UIResult => {
    const hasProblem = responses.hasProblem === true;
    
    return {
      status: hasProblem ? 'NEEDS_ATTENTION' : 'OK',
      hasProblem: hasProblem,
      recommendations: hasProblem 
        ? ["ICOPE-C13-S13.1-UrinaryIncontinence-Overview"] 
        : []
    };
  };

  const handlePrimaryResponse = (hasProblem: boolean) => {
    const updatedResponses = { ...responses, hasProblem };
    setResponses(updatedResponses);
    setCurrentStep('results');
  };

  const handleCarerCorrection = (problemCorrect: boolean) => {
    // 基于照护者修正重新评估尿失禁状况
    const hasProblem = !problemCorrect;
    
    const correctedResult: UIResult = {
      status: hasProblem ? 'NEEDS_ATTENTION' : 'OK',
      hasProblem: hasProblem,
      recommendations: hasProblem 
        ? ["ICOPE-C13-S13.1-UrinaryIncontinence-Overview"] 
        : []
    };
    
    if (!hasCompletedRef.current) {
      hasCompletedRef.current = true;
      onComplete(correctedResult);
      setCurrentStep('completed');
    }
  };

  useEffect(() => {
    if ((currentStep === 'completed' || currentStep === 'results') && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      
      const result = evaluateUrinaryIncontinence();
      onComplete(result);
      
      const delay = currentStep === 'completed' ? 1500 : 3000;
      const timer = setTimeout(() => {
        onNext();
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [currentStep, onComplete, onNext]);

  const renderIntro = () => (
    <div className="text-center space-y-4 p-4">
      <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">尿失禁评估</h2>
      <p className="text-gray-600">评估膀胱控制功能状况</p>
      
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-left">
        <h3 className="font-semibold text-gray-900 mb-2">关于此评估：</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• 基于WHO ICOPE标准</li>
          <li>• 这是一个敏感话题的评估</li>
          <li>• 您的回答完全保密</li>
          <li>• 如不愿回答可以跳过此评估</li>
        </ul>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
        <p className="text-sm text-yellow-800">
          <strong>隐私保护：</strong> 此评估涉及敏感健康信息，您的回答仅用于健康评估，绝对保密
        </p>
      </div>
      
      <div className="flex flex-col gap-2">
        <button 
          onClick={() => setCurrentStep('primary_question')} 
          className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-colors"
        >
          开始评估
        </button>
        
        <button 
          onClick={() => {
            // 跳过评估，设置为无问题
            const result: UIResult = { 
              status: 'OK', 
              hasProblem: false, 
              recommendations: [] 
            };
            onComplete(result);
            setCurrentStep('completed');
          }}
          className="w-full bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
        >
          跳过此评估
        </button>
      </div>
    </div>
  );

  const renderPrimaryQuestion = () => (
    <div className="p-4 space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl font-bold text-indigo-600">?</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">尿失禁评估</h2>
        <p className="text-gray-600">核心问题</p>
      </div>

      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          您是否有尿液控制方面的困难？
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          包括不自主漏尿、急迫性尿失禁等情况
        </p>
        
        <div className="space-y-3">
          <button
            onClick={() => handlePrimaryResponse(false)}
            className="w-full p-4 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-green-800">没有，我没有这方面困难</span>
              <span className="text-sm text-green-600">正常</span>
            </div>
          </button>
          
          <button
            onClick={() => handlePrimaryResponse(true)}
            className="w-full p-4 text-left bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-red-800">是的，我有这方面困难</span>
              <span className="text-sm text-red-600">需要关注</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderResults = () => {
    const result = evaluateUrinaryIncontinence();
    const hasProblem = result.hasProblem;
    
    const getStatusColor = (hasProb: boolean) => {
      return hasProb ? 'text-red-600' : 'text-green-600';
    };
    
    const getStatusBg = (hasProb: boolean) => {
      return hasProb ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200';
    };

    const getStatusText = (hasProb: boolean) => {
      return hasProb ? '需要关注' : '状况良好';
    };
    
    return (
      <div className="p-4 space-y-6">
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            hasProblem ? 'bg-red-100' : 'bg-green-100'
          }`}>
            <svg className={`w-8 h-8 ${getStatusColor(hasProblem)}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">评估完成</h2>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">尿失禁评估结果：</h3>
          
          <div className={`p-4 rounded-lg border ${getStatusBg(hasProblem)} mb-4`}>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getStatusColor(hasProblem)} mb-1`}>
                {getStatusText(hasProblem)}
              </div>
              <p className="text-gray-600">膀胱控制状况</p>
            </div>
          </div>

          <div className={`p-3 rounded ${getStatusBg(hasProblem)}`}>
            <h4 className={`font-semibold ${getStatusColor(hasProblem)} mb-2`}>建议：</h4>
            <div className={`text-sm ${getStatusColor(hasProblem).replace('-600', '-700')} space-y-1`}>
              {!hasProblem && (
                <p>您的膀胱控制功能正常，请继续保持良好的生活习惯。</p>
              )}
              {hasProblem && (
                <>
                  <p>检测到膀胱控制问题，建议：</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>咨询医疗专业人员，进行详细评估</li>
                    <li>进行盆底肌训练（凯格尔运动）</li>
                    <li>调整饮水习惯和生活方式</li>
                    <li>保持理想体重</li>
                  </ul>
                </>
              )}
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">3秒后自动进入下一项评估...</p>
          </div>
        </div>
      </div>
    );
  };

  const renderCarerCorrection = () => {
    const result = evaluateUrinaryIncontinence();
    const hasProblem = result.hasProblem;
    
    return (
      <div className="p-4 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">照护者答案确认</h2>
          <p className="text-gray-600">请照护者根据实际情况确认答案正确性</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">系统评估结果回顾</h3>
            
            <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
              <div className={`flex items-center justify-between p-3 rounded-lg ${
                hasProblem ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
              }`}>
                <span className="font-medium">膀胱控制问题</span>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm ${hasProblem ? 'text-red-700' : 'text-green-700'}`}>
                    {hasProblem ? '有困难' : '无问题'}
                  </span>
                  <span className={`text-lg ${hasProblem ? 'text-red-600' : 'text-green-600'}`}>
                    {hasProblem ? '❌' : '✅'}
                  </span>
                </div>
              </div>
              
              <div className={`p-3 rounded-lg text-center mt-3 ${hasProblem ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                <span className={`font-medium ${hasProblem ? 'text-red-800' : 'text-green-800'}`}>
                  系统评估：{hasProblem ? '❌ 需要关注' : '✅ 状况良好'}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">照护者确认</h3>
            <p className="text-gray-700 mb-4">
              请照护者根据对老年人实际情况的了解，确认以下问题：
            </p>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">膀胱控制功能确认</h4>
              <p className="text-sm text-gray-600 mb-3">是否观察到老年人有尿液控制方面的困难？</p>
              <div className="space-y-2">
                <button
                  onClick={() => setCarerCorrection(prev => ({ ...prev, problemCorrect: true }))}
                  className={`w-full p-3 text-left rounded-lg border transition-all ${
                    carerCorrection.problemCorrect === true
                      ? 'bg-green-50 border-green-300 text-green-800'
                      : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      carerCorrection.problemCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                    }`}>
                      {carerCorrection.problemCorrect === true && (
                        <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="font-medium">✅ 膀胱控制正常，无明显问题</span>
                  </div>
                </button>
                
                <button
                  onClick={() => setCarerCorrection(prev => ({ ...prev, problemCorrect: false }))}
                  className={`w-full p-3 text-left rounded-lg border transition-all ${
                    carerCorrection.problemCorrect === false
                      ? 'bg-red-50 border-red-300 text-red-800'
                      : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      carerCorrection.problemCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                    }`}>
                      {carerCorrection.problemCorrect === false && (
                        <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="font-medium">❌ 有膀胱控制困难</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => handleCarerCorrection(carerCorrection.problemCorrect!)}
            disabled={carerCorrection.problemCorrect === null}
            className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 disabled:bg-gray-300 transition-colors font-medium"
          >
            确认修正，完成评估
          </button>
        </div>
      </div>
    );
  };

  const renderCompleted = () => (
    <div className="text-center p-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">尿失禁评估完成</h2>
      <p className="text-gray-600">正在转到下一项...</p>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 'intro': return renderIntro();
      case 'primary_question': return renderPrimaryQuestion();
      case 'results': return renderResults();
      case 'carer_correction': return renderCarerCorrection();
      case 'completed': return renderCompleted();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {renderStep()}
      </div>
    </div>
  );
};

export default UrinaryIncontinenceAssessment;